/**
 * Environment Variables and Constants
 *
 * Centralized configuration for all environment variables used throughout the application.
 * All environment variables must be prefixed with VITE_ to be accessible in the client.
 */

// ==================================================
// REQUIRED: Modal API Configuration
// ==================================================

export const OCR_SERVICE_URL = import.meta.env.VITE_OCR_SERVICE_URL;
export const ZENDESK_SERVICE_URL = import.meta.env.VITE_ZENDESK_SERVICE_URL;
export const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || "development";

// ==================================================
// API Configuration
// ==================================================

export const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000;
export const MAX_FILE_SIZE =
  Number(import.meta.env.VITE_MAX_FILE_SIZE) || 52428800; // 50MB

// ==================================================
// Contact Information
// ==================================================

export const SUPPORT_EMAIL =
  import.meta.env.VITE_SUPPORT_EMAIL || "info@getdianahr.com";

// ==================================================
// Feature Flags
// ==================================================

export const ENABLE_DEBUG = import.meta.env.VITE_ENABLE_DEBUG === "true";
export const ENABLE_ANALYTICS =
  import.meta.env.VITE_ENABLE_ANALYTICS === "true";
export const ENABLE_ERROR_TRACKING =
  import.meta.env.VITE_ENABLE_ERROR_TRACKING === "true";

// ==================================================
// Analytics Configuration (PostHog)
// ==================================================

export const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
export const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com";

// ==================================================
// Error Tracking Configuration (Sentry)
// ==================================================

export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
export const SENTRY_ENVIRONMENT =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || ENVIRONMENT;
export const SENTRY_ORG = import.meta.env.VITE_SENTRY_ORG || "dianahr";
export const SENTRY_PROJECT =
  import.meta.env.VITE_SENTRY_PROJECT || "diana-state-notice-tool";

// ==================================================
// Application Configuration
// ==================================================

export const APP_NAME =
  import.meta.env.VITE_APP_NAME || "Diana State Notice Tool";
export const APP_BASE_URL =
  import.meta.env.VITE_APP_BASE_URL || "http://localhost:5173";
export const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || "local-dev";
export const DEPLOY_ENV = import.meta.env.VITE_DEPLOY_ENV || "local";

// ==================================================
// Development Configuration
// ==================================================

export const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === "true";

// ==================================================
// Validation Constants
// ==================================================

export const VALIDATION = {
  MAX_FILE_SIZE_MB: Math.floor(MAX_FILE_SIZE / 1024 / 1024),
  ALLOWED_FILE_TYPES: ["application/pdf"],
  ALLOWED_FILE_EXTENSIONS: [".pdf"],
  MAX_FILENAME_LENGTH: 255,
  MIN_PROCESSING_TIME: 1000, // 1 second minimum for UX
} as const;

// ==================================================
// External URLs
// ==================================================

export const HUBSPOT_MEETING_URL = "https://www.getdianahr.com/demo-booking";

// ==================================================
// UI Constants
// ==================================================

export const UI_CONSTANTS = {
  PROCESSING_DELAY: 2000, // 2 seconds before auto-submitting to HR
  NOTIFICATION_DURATION: 5000, // 5 seconds
  ANIMATION_DURATION: 300, // 300ms
  DEBOUNCE_DELAY: 500, // 500ms for form validation
} as const;

// ==================================================
// HTTP Status Codes
// ==================================================

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
} as const;

// ==================================================
// Environment Validation
// ==================================================

/**
 * Validates that required environment variables are present
 * @throws Error if required environment variables are missing
 */
export function validateEnvironment(): void {
  const requiredVars = [
    { name: "VITE_OCR_SERVICE_URL", value: OCR_SERVICE_URL },
    { name: "VITE_ZENDESK_SERVICE_URL", value: ZENDESK_SERVICE_URL },
  ];

  const missingVars = requiredVars.filter(({ value }) => !value);

  if (missingVars.length > 0) {
    const missingNames = missingVars.map(({ name }) => name).join(", ");
    throw new Error(
      `Missing required environment variables: ${missingNames}. ` +
        "Please check your .env.local file and ensure all required variables are set.",
    );
  }
}

// ==================================================
// Environment Information (for debugging)
// ==================================================

export const ENV_INFO = {
  NODE_ENV: import.meta.env.MODE,
  IS_DEV: import.meta.env.DEV,
  IS_PROD: import.meta.env.PROD,
  ENVIRONMENT,
  BUILD_VERSION,
  DEPLOY_ENV,
} as const;

// ==================================================
// Export default configuration object
// ==================================================

export default {
  // API
  OCR_SERVICE_URL,
  ZENDESK_SERVICE_URL,
  API_TIMEOUT,
  MAX_FILE_SIZE,

  // Contact
  SUPPORT_EMAIL,

  // Features
  ENABLE_DEBUG,
  ENABLE_ANALYTICS,
  ENABLE_ERROR_TRACKING,

  // Analytics
  POSTHOG_KEY,
  POSTHOG_HOST,

  // Error Tracking
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_ORG,
  SENTRY_PROJECT,

  // App
  APP_NAME,
  APP_BASE_URL,
  BUILD_VERSION,
  ENVIRONMENT,

  // Constants
  VALIDATION,
  UI_CONSTANTS,
  HTTP_STATUS,
  ENV_INFO,
} as const;
